import { CheckOutlined } from '@ant-design/icons';
import { Col, Collapse, Row, Space, Table, TableColumnsType } from 'antd';
import CollapsePanel from 'antd/es/collapse/CollapsePanel';
import { ColumnsType } from 'antd/es/table';
import { TFunction } from 'i18next';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { TicketDetailContext } from 'src/contexts/ticketDetail.context';
import { IAccessForm, IAccessFormDetail, IWorkDetail } from 'src/types/helpdesk/accessForm.type';
import { formatDate } from 'src/utils/utils';
import { v4 as uuid } from 'uuid';

interface ExpandedDataTypeSubSystem {
  key: React.Key;
  subSystemName: string | undefined;
  functionName: string | undefined;
  detail: IAccessFormDetail;
}

const expandedRowWorkDescriptionOfAccessFormRender = (
  record: Partial<IAccessForm>,
  t: TFunction<'translation', undefined, 'translation'>
) => {
  const columnsWorkDescription: TableColumnsType<IWorkDetail> = [
    {
      title: t('columnTitle.workDescription.id'),
      dataIndex: 'workDescriptionId',
      width: '40%'
    },
    {
      title: t('columnTitle.workDescription.name'),
      dataIndex: 'workDescriptionName',
      width: '50%'
    }
  ];

  return (
    <Table columns={columnsWorkDescription} dataSource={record.workDetails} pagination={false} bordered size='middle' />
  );
};

const expandedRowSubSystemOfAccessFormRender = (
  record: Partial<IAccessForm>,
  t: TFunction<'translation', undefined, 'translation'>
) => {
  const columnsSubSystem: TableColumnsType<ExpandedDataTypeSubSystem> = [
    {
      title: t('columnTitle.accessFormDetail.subSystem'),
      dataIndex: 'subSystemName'
    },
    {
      title: t('columnTitle.accessFormDetail.function'),
      dataIndex: 'functionName'
    },
    {
      title: t('columnTitle.accessFormDetail.permission'),
      width: '40%',
      render: (_, record) => (
        <Row gutter={[_, 4]}>
          <Col span={8}>
            <Space>
              <CheckOutlined style={{ color: record.detail?.readOnly ? '#52c41a' : 'rgba(0, 0, 0, 0.25)' }} />
              {t(`columnTitle.accessFormDetail.readOnly`)}
            </Space>
          </Col>
          <Col span={8}>
            <Space>
              <CheckOutlined style={{ color: record.detail?.create ? '#52c41a' : 'rgba(0, 0, 0, 0.25)' }} />
              {t(`columnTitle.accessFormDetail.create`)}
            </Space>
          </Col>
          <Col span={8}>
            <Space>
              <CheckOutlined style={{ color: record.detail?.update ? '#52c41a' : 'rgba(0, 0, 0, 0.25)' }} />
              {t(`columnTitle.accessFormDetail.update`)}
            </Space>
          </Col>
          <Col span={8}>
            <Space>
              <CheckOutlined style={{ color: record.detail?.delete ? '#52c41a' : 'rgba(0, 0, 0, 0.25)' }} />
              {t(`columnTitle.accessFormDetail.delete`)}
            </Space>
          </Col>
          <Col span={8}>
            <Space>
              <CheckOutlined style={{ color: record.detail?.printOut ? '#52c41a' : 'rgba(0, 0, 0, 0.25)' }} />
              {t(`columnTitle.accessFormDetail.printOut`)}
            </Space>
          </Col>
          <Col span={8}>
            <Space>
              <CheckOutlined style={{ color: record.detail?.approval ? '#52c41a' : 'rgba(0, 0, 0, 0.25)' }} />
              {t(`columnTitle.accessFormDetail.approval`)}
            </Space>
          </Col>
        </Row>
      )
    }
  ];

  const dataSubSystem = record.details?.map((item) => ({
    key: item.id ?? uuid(),
    subSystemName: item.subSystemName,
    functionName: item.functionName,
    detail: item
  }));

  return <Table columns={columnsSubSystem} dataSource={dataSubSystem} pagination={false} bordered size='middle' />;
};

const columnsAccessForm = function (t: TFunction<'translation', undefined, 'translation'>): ColumnsType<IAccessForm> {
  return [
    {
      title: t('columnTitle.accessForm.id'),
      sorter: true,
      dataIndex: 'id'
    },
    {
      title: t('columnTitle.accessForm.forPerson'),
      sorter: true,
      dataIndex: 'forPerson',
      render: (_: string, record: IAccessForm) => record.forPerson?.fullName
    },
    {
      title: t('columnTitle.accessForm.date'),
      dataIndex: 'date',
      render: (_: string, record: IAccessForm) => formatDate(record.date)
    }
  ];
};

const AccessForm = (): JSX.Element => {
  const { t } = useTranslation();
  const { ticket } = useContext(TicketDetailContext);
  return (
    <Table
      columns={columnsAccessForm(t)}
      bordered
      rowKey={(record) => record.id}
      dataSource={ticket?.data.accessForms}
      pagination={false}
      // loading={ticketQuery?.isFetching}
      expandable={{
        expandedRowRender: (record) => (
          <Collapse size='small' bordered={true} defaultActiveKey={[0, 1]}>
            <CollapsePanel header={t('card.workDescription')} key={0}>
              {expandedRowWorkDescriptionOfAccessFormRender(record, t)}
            </CollapsePanel>
            <CollapsePanel header={t('card.subSystem')} key={1}>
              {expandedRowSubSystemOfAccessFormRender(record, t)}
            </CollapsePanel>
          </Collapse>
        ),
        rowExpandable: (record) => record.details !== undefined && record.details.length > 0
      }}
    />
  );
};
export default AccessForm;
